import { Moon, Sun } from "lucide-react";
import { useThemeMode } from "@hooks/useThemeMode";
import { cn } from "../../utils";
import { useTranslation } from "../../i18n";

interface ThemeToggleProps {
  className?: string;
}

/**
 * One-click light/dark switcher. Reads + persists the user's choice via
 * `useThemeMode`; the actual color swap is handled by the `.dark` class on
 * <html> and the CSS variables defined in `src/index.css`.
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({ className }) => {
  const { toggle, resolvedTheme } = useThemeMode();
  const { t } = useTranslation();
  const isDark = resolvedTheme === "dark";
  const label = t(isDark ? "website.theme.light" : "website.theme.dark");

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      aria-pressed={isDark}
      title={label}
      className={cn(
        "inline-flex h-9 w-9 items-center justify-center rounded-md",
        "text-brand-on-brand/90 transition-colors",
        "hover:bg-brand-on-brand/15 hover:text-brand-on-brand",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-on-brand/40",
        className,
      )}
    >
      <Sun className="hidden h-4 w-4 dark:block" />
      <Moon className="block h-4 w-4 dark:hidden" />
    </button>
  );
};

export default ThemeToggle;
